import { useContext } from 'react'

import OutsideClickHandler from 'react-outside-click-handler'
import { shallowEqual, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import Flex from 'styled-flex-component'

import Breakpoint from '@components/UI/Breakpoint'
import BudgetInformationPanel from '@components/UI/BudgetInformationPanel/BudgetInformationPanel'
import { ConnectedTrackedRoute } from '@components/UI/TrackedRoute'
import { INBOX_FILTER } from '@constants'
import { InformationTooltipContext } from '@context/InformationTooltip'
import { EventTrackingNames, MemberRoleTypes } from '@enums'
import { IApplicationState } from '@store'
import {
  selectCampaignBudgetRemaining,
  selectCampaignBudgetSpent,
  selectCampaignTotalCurrency,
  selectIsPriceHidden,
  selectIsSocialCampaign,
  selectIsTraditional,
  selectPaymentMethod,
  selectRole,
} from '@store/campaigns'
import { selectCampaignId, selectCurrentSubmissionFilter } from '@store/router'
import { selectSubmissionsTotalValue } from '@store/submissions'
import { Container } from '@tribegroup'
import { pxToRem } from '@utils'
import CampaignBudgetHoverableIcon from './HoverableIcon'
import CampaignBudgetInboxValue from './InboxValue'
import CampaignBudgetTipContent, { CAMPAIGN_BUDGET_TIP_CONTENT_TIP_ID } from './TipContent'

export const CampaignBudget: React.FC = () => {
  const match = useRouteMatch()
  const campaignId = selectCampaignId(match) as number
  const {
    currency,
    inboxValue,
    isOwner,
    isPriceHidden,
    isSocialCampaign,
    isTraditional,
    paymentMethod,
    remaining,
    spent,
  } = useSelector((state: IApplicationState) => {
    return {
      isSocialCampaign: selectIsSocialCampaign(state, campaignId),
      remaining: selectCampaignBudgetRemaining(state, campaignId),
      spent: selectCampaignBudgetSpent(state, campaignId),
      currency: selectCampaignTotalCurrency(state, campaignId),
      paymentMethod: selectPaymentMethod(state, campaignId),
      inboxValue: selectSubmissionsTotalValue(state, campaignId),
      isPriceHidden: selectIsPriceHidden(state, campaignId),
      isTraditional: selectIsTraditional(state, campaignId),
      isOwner: selectRole(state, campaignId) === MemberRoleTypes.Owner,
    }
  }, shallowEqual)

  const { showTooltip, hideTooltip, tooltipVisible, content } = useContext(
    InformationTooltipContext,
  )
  const isBudgetContent = content && content['key'] === CAMPAIGN_BUDGET_TIP_CONTENT_TIP_ID
  const submissionFilter = useSelector(selectCurrentSubmissionFilter)

  const showInboxValue = inboxValue && submissionFilter === INBOX_FILTER

  const showBudgetBreakdown = isTraditional || isOwner

  const showBudget = isTraditional || !isPriceHidden

  if (!showBudget) {
    return null
  }

  const onOutsideClick = () => {
    if (tooltipVisible && isBudgetContent) {
      resetTooltip()
    }
  }

  const onInsideClick = () => {
    if (tooltipVisible && isBudgetContent) {
      resetTooltip()
      return
    }

    const target = document.querySelector('[data-icon-glyph="information"]') as HTMLElement
    const { left, top } = target.getBoundingClientRect()
    showTooltip(
      <CampaignBudgetTipContent
        campaignId={campaignId}
        paymentMethod={paymentMethod}
        key={CAMPAIGN_BUDGET_TIP_CONTENT_TIP_ID}
      />,
      {
        x: pxToRem(left),
        y: pxToRem(top + window.scrollY + 16),
      },
      'right',
      24,
    )
  }

  const resetTooltip = () => {
    hideTooltip()
  }

  const inboxAmount = inboxValue?.inbox_cents
  const inboxCurrency = inboxValue?.inbox_currency

  return (
    <ConnectedTrackedRoute
      eventName={EventTrackingNames.BudgetPanelTooltip}
      disableTracking={!isSocialCampaign}
      campaignId={campaignId}
      additionalProperties={{
        budget_type: paymentMethod,
        action: tooltipVisible ? 'hide' : 'show',
      }}
    >
      <OutsideClickHandler onOutsideClick={onOutsideClick}>
        <BudgetInformationPanel
          remaining={remaining.amount}
          spent={spent.amount}
          currency={remaining.currency || currency}
          onClick={showBudgetBreakdown ? onInsideClick : undefined}
          secondRowContent={
            inboxValue && (
              <Breakpoint mobile tabletPortrait>
                <Container topOuterSpacing={0.5}>
                  <CampaignBudgetInboxValue amount={inboxAmount} currency={inboxCurrency} />
                </Container>
              </Breakpoint>
            )
          }
        >
          <Flex justifyBetween alignCenter>
            <Container topOuterSpacing={0.25} leftOuterSpacing={0.5} width={1} inlineBlock>
              {showBudgetBreakdown && <CampaignBudgetHoverableIcon campaignId={campaignId} />}
            </Container>
            {showInboxValue && (
              <Breakpoint tabletLandscape desktop>
                <CampaignBudgetInboxValue reversed amount={inboxAmount} currency={inboxCurrency} />
              </Breakpoint>
            )}
          </Flex>
        </BudgetInformationPanel>
      </OutsideClickHandler>
    </ConnectedTrackedRoute>
  )
}

export default CampaignBudget
